import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  cartCreate,
  cartGet,
  cartLinesAdd,
  cartLinesUpdate,
  cartLinesRemove,
} from "@/lib/shopify";

const STORAGE_KEY = "vfi_shopify_cart_id";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mutating, setMutating] = useState(false);

  // Boot: hydrate cart from existing cartId or create a new one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = localStorage.getItem(STORAGE_KEY);
        if (existing) {
          const got = await cartGet(existing);
          // Shopify returns null if cart was completed/expired
          if (got && !cancelled) {
            setCart(got);
            setLoading(false);
            return;
          }
        }
        const fresh = await cartCreate();
        if (!cancelled) {
          localStorage.setItem(STORAGE_KEY, fresh.id);
          setCart(fresh);
        }
      } catch (e) {
        console.error("[Shopify] failed to init cart", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ensureCart = useCallback(async () => {
    if (cart) return cart;
    const fresh = await cartCreate();
    localStorage.setItem(STORAGE_KEY, fresh.id);
    setCart(fresh);
    return fresh;
  }, [cart]);

  const addItem = useCallback(
    async (variant, product, quantity = 1) => {
      setMutating(true);
      try {
        const c = await ensureCart();
        const updated = await cartLinesAdd(c.id, [
          { variant, product, quantity },
        ]);
        setCart(updated);
        setDrawerOpen(true);
      } finally {
        setMutating(false);
      }
    },
    [ensureCart]
  );

  const updateQty = useCallback(
    async (lineId, quantity) => {
      if (!cart) return;
      setMutating(true);
      try {
        const updated = await cartLinesUpdate(cart.id, [
          { id: lineId, quantity },
        ]);
        setCart(updated);
      } finally {
        setMutating(false);
      }
    },
    [cart]
  );

  const removeItem = useCallback(
    async (lineId) => {
      if (!cart) return;
      setMutating(true);
      try {
        const updated = await cartLinesRemove(cart.id, [lineId]);
        setCart(updated);
      } finally {
        setMutating(false);
      }
    },
    [cart]
  );

  const goToCheckout = useCallback(() => {
    if (cart?.checkoutUrl) {
      window.location.href = cart.checkoutUrl;
    }
  }, [cart]);

  const value = {
    cart,
    loading,
    mutating,
    drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    addItem,
    updateQty,
    removeItem,
    goToCheckout,
    totalQuantity: cart?.totalQuantity ?? 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within <CartProvider>");
  return ctx;
}
